import {useNavigate, useParams} from "react-router-dom";
import {Alert, Autocomplete, Box, Divider, Stack} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {AgeCategoryService} from "crqlar-react-common/dist/services/ageCategory/ageCategory.service";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {AgeCategory, AgeCategoryType} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import LocalizedTextField from "crqlar-react-common/dist/components/LocalizedTextField";
import NewColorPicker from "crqlar-react-common/dist/components/NewColorPicker";
import {useQueryClient} from "react-query";
import {useAllAgeCategories} from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";

type AliasCoverageRowStatus =
    'included' |
    'notCovered' |
    'overlap'

type AliasCoverageRow = {
    key: string
    minAge: number | null
    maxAge: number | null
    category?: AgeCategory
    status: AliasCoverageRowStatus
}

const hasValidAgeBound = (age?: number | null) =>
    age == null || (Number.isInteger(age) && age >= 0)

const hasValidAgeRange = (category: AgeCategory) =>
    hasValidAgeBound(category.minAge) &&
    hasValidAgeBound(category.maxAge) &&
    !(
        category.minAge != null &&
        category.maxAge != null &&
        category.maxAge < category.minAge
    )

const sortAgeCategories = (ageCategories: AgeCategory[]) =>
    [...ageCategories].sort(
        (first, second) =>
            (first.minAge ?? 0) - (second.minAge ?? 0) ||
            (first.maxAge ?? Number.MAX_SAFE_INTEGER) - (second.maxAge ?? Number.MAX_SAFE_INTEGER) ||
            (first.name ?? '').localeCompare(second.name ?? '')
    )

const createAliasCoverageRows = (ageCategories: AgeCategory[]): AliasCoverageRow[] => {
    const validAliasedCategories = sortAgeCategories(
        ageCategories.filter(category => category.alias?.trim() && hasValidAgeRange(category))
    )

    if (!validAliasedCategories.length) return []

    const rows: AliasCoverageRow[] = []
    let nextUncoveredAge = 0

    const addGap = (minAge: number, maxAge: number | null) => {
        rows.push({
            key: `gap-${minAge}-${maxAge ?? 'open'}`,
            minAge,
            maxAge,
            status: 'notCovered'
        })
    }

    validAliasedCategories.forEach((category, index) => {
        const minAge = category.minAge ?? 0
        const maxAge = category.maxAge ?? null

        if (nextUncoveredAge !== Number.POSITIVE_INFINITY && minAge > nextUncoveredAge) {
            addGap(nextUncoveredAge, minAge - 1)
        }

        const overlapsPreviousRange =
            nextUncoveredAge === Number.POSITIVE_INFINITY ||
            minAge < nextUncoveredAge

        rows.push({
            key: category.id ?? `included-${index}`,
            minAge: category.minAge ?? null,
            maxAge,
            category,
            status: overlapsPreviousRange ? 'overlap' : 'included'
        })

        nextUncoveredAge = maxAge == null
            ? Number.POSITIVE_INFINITY
            : Math.max(nextUncoveredAge, maxAge + 1)
    })

    if (nextUncoveredAge !== Number.POSITIVE_INFINITY) {
        addGap(nextUncoveredAge, null)
    }

    return rows
}

const formatAgeRange = (minAge: number | null, maxAge: number | null, allAgesLabel: string) => {
    if (minAge == null && maxAge == null) return allAgesLabel
    if (minAge == null) return `≤${maxAge}`
    return maxAge == null ? `${minAge}+` : `${minAge}–${maxAge}`
}

const AgeCategoryDetails = () => {

    //region State

    useTrackScreenVisit('system age category details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const allAgeCategories = useAllAgeCategories()

    const ageCategoryData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return AgeCategoryService.getAgeCategoryById(id)
            } else {
                return {
                    alias: null,
                    color: null
                }
            }
        },
        create: AgeCategoryService.createAgeCategory,
        update: AgeCategoryService.updateAgeCategory,
        delete: (id) => AgeCategoryService.deleteAgeCategory({id: [id]}),
        onDeleted: () => {
            void queryClient.invalidateQueries('allAgeCategories')
            navigate('/system/ageCategories', {replace: true})
        },
        onSaved: (ageCategory, wasCreateAction, prev) => {
            void queryClient.invalidateQueries('allAgeCategories')
            if (wasCreateAction) {
                navigate(`/system/ageCategories/${ageCategory.id}`, {replace: true})
            }
        },
        isValid: (ageCategory) => {
            return !!ageCategory.name
        },
        cleanupBeforeSave: (ageCategory) => {
            return {
                ...ageCategory,
                name: ageCategory.name?.trim(),
                alias: ageCategory.alias?.trim() || null,
                color: ageCategory.color ?? null
            }
        }
    }, id)

    const ageCategory = ageCategoryData.item
    const setAgeCategory = ageCategoryData.setItem
    const nameMissing = isRequiredTextMissing(ageCategoryData, ageCategory.name)
    const currentCategoryHasValues = Boolean(
        ageCategory.id ||
        ageCategory.name ||
        ageCategory.alias?.trim() ||
        ageCategory.minAge != null ||
        ageCategory.maxAge != null
    )
    const categoriesForAliasValidation = [
        ...allAgeCategories.filter(category => category.id !== ageCategory.id),
        ...(currentCategoryHasValues ? [ageCategory] : [])
    ]
    const aliasCoverageRows = createAliasCoverageRows(categoriesForAliasValidation)

    //endregion Sate

    //region Handlers

    const updateField = (field: keyof AgeCategory, value: any) => {
        setAgeCategory({...ageCategory, [field]: value})
    }

    const aliasCoverageStatus = (status: AliasCoverageRowStatus) => {
        return {
            included: {label: t('Included'), color: 'success.main'},
            notCovered: {label: t('Not covered'), color: 'error.main'},
            overlap: {label: t('Overlapping range'), color: 'error.main'}
        }[status]
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={ageCategory.name}
            data={ageCategoryData}
            back={() => navigate('/system/ageCategories')}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this age category in reservation and setup screens.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={ageCategory.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Min age')}
                    tooltip={t('Lowest age included in this category. Leave empty for no lower age limit.')}
                >
                    <TextField
                        id={'min-age'}
                        size={'small'}
                        value={ageCategory.minAge ?? ''}
                        onChange={(e) => updateField(
                            'minAge',
                            e.target.value === '' ? null : parseInt(e.target.value)
                        )}
                        fullWidth
                        type={'number'}
                    />
                </Property>

                <Property
                    title={t('Max age')}
                    tooltip={t('Highest age included in this category. Leave empty for no upper age limit.')}
                >
                    <TextField
                        id={'maxAge'}
                        size={'small'}
                        value={ageCategory.maxAge ?? ''}
                        onChange={(e) => updateField(
                            'maxAge',
                            e.target.value === '' ? null : parseInt(e.target.value)
                        )}
                        fullWidth
                        type={'number'}
                    />
                </Property>

                <Property
                    title={t('Age category type')}
                    tooltip={t('Controls how reservations, arrival lists, and floor plans group this category as adult, teenager, child, or infant.')}
                >
                    <Autocomplete
                        id={'age-category-type'}
                        options={Object.values(AgeCategoryType)}
                        getOptionLabel={(option: AgeCategoryType) => option ? t(`Age category type ${option}`) : t('(Not set)')}
                        value={ageCategory.type || null}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                            />
                        )}
                        onChange={(e, value) => {
                            updateField('type', value)
                        }}
                        fullWidth
                        size={'small'}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const advancedSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Advanced')}
            </Typography>

            <Alert severity={'info'} variant={'outlined'} sx={{bgcolor: 'transparent'}}>
                <Typography variant={'body1'}>
                    {t('An alias is a custom label for an existing age range. As soon as one alias is set, the aliased categories must together cover every age without gaps or overlaps. Leave Min age empty for no lower limit and Max age empty for no upper limit.')}
                </Typography>
            </Alert>

            <Stack gap={1}>
                <Property
                    title={t('Alias')}
                    tooltip={t('Custom label shown for this age category in guest counts and reservation views.')}
                >
                    <TextField
                        id={'alias'}
                        size={'small'}
                        value={ageCategory.alias ?? ''}
                        onChange={(e) => updateField('alias', e.target.value)}
                        fullWidth
                    />
                </Property>

                <Property
                    title={t('Color')}
                    tooltip={t('Custom color used instead of the default age category color.')}
                >
                    <NewColorPicker
                        id={'color'}
                        value={ageCategory.color}
                        onChange={(color) => updateField('color', color ?? null)}
                    />
                </Property>
            </Stack>

            {aliasCoverageRows.length > 0 && (
                <Stack gap={1}>
                    <Typography variant={'h6'}>
                        {t('Alias coverage')}
                    </Typography>

                    <Box
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflowX: 'auto'
                        }}
                    >
                        <Box sx={{minWidth: 620}}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '100px minmax(180px, 2fr) minmax(120px, 1fr) minmax(120px, 1fr)',
                                    gap: 2,
                                    px: 1.5,
                                    py: 1,
                                    borderBottom: 1,
                                    borderColor: 'divider'
                                }}
                            >
                                <Typography variant={'body2'} fontWeight={600}>{t('Age range')}</Typography>
                                <Typography variant={'body2'} fontWeight={600}>{t('Category')}</Typography>
                                <Typography variant={'body2'} fontWeight={600}>{t('Alias')}</Typography>
                                <Typography variant={'body2'} fontWeight={600}>{t('Status')}</Typography>
                            </Box>

                            {aliasCoverageRows.map(row => {
                                const status = aliasCoverageStatus(row.status)
                                const isCurrentCategory = row.category === ageCategory

                                return (
                                    <Box
                                        key={row.key}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '100px minmax(180px, 2fr) minmax(120px, 1fr) minmax(120px, 1fr)',
                                            gap: 2,
                                            alignItems: 'center',
                                            px: 1.5,
                                            py: 1,
                                            '&:not(:last-child)': {
                                                borderBottom: 1,
                                                borderColor: 'divider'
                                            }
                                        }}
                                    >
                                        <Typography variant={'body1'}>
                                            {formatAgeRange(row.minAge, row.maxAge, t('All ages'))}
                                        </Typography>
                                        <Typography variant={'body1'}>
                                            {row.category?.name?.trim() || '—'}
                                            {isCurrentCategory && ` (${t('Current')})`}
                                        </Typography>
                                        <Typography variant={'body1'}>
                                            {row.category?.alias?.trim() || '—'}
                                        </Typography>
                                        <Typography variant={'body1'} color={status.color} fontWeight={500}>
                                            {status.label}
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Box>
                </Stack>
            )}
        </Stack>
    )

    const translationsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Translations')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name translations')}
                    tooltip={t('Localized labels shown when the interface or guest-facing content uses another language.')}
                >
                    <LocalizedTextField
                        id={'nameLocalized'}
                        size={'small'}
                        value={ageCategory.nameLocalized}
                        onChange={(e) => updateField('nameLocalized', e)}
                        fullWidth
                        required
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete age category')}
                description={t('Deleting age categories will remove them from all associated reservations')}
                buttonLabel={t('Delete')}
                loading={ageCategoryData.saving}
                onDelete={() => ageCategoryData.removeNow()}
            />
            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {ageCategory.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}

            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    mx: 'auto',
                    mt: 3,
                    gap: 6,
                }}
            >
                {basicInfoSection}
                <Divider/>
                {advancedSection}
                <Divider/>
                {translationsSection}

                {
                    !!ageCategory.id &&
                    <>
                        <Divider/>
                        {dangerZoneSection}
                    </>
                }
            </Stack>
        </Box>
    );
}

export default AgeCategoryDetails;
